<div class="relative">

  <!-- Trigger button -->
  <button
    (click)="toggle()"
    class="flex items-center gap-2 cursor-pointer transition-colors duration-150
           px-2 py-1 hover:bg-white/10 focus:outline-none"
    [attr.aria-expanded]="open()"
    aria-haspopup="true"
  >
    <!-- Avatar initials -->
    <span class="w-8 h-8 rounded-full bg-white/20 text-white text-xs font-semibold
                 flex items-center justify-center select-none">
      {{ initials() }}
    </span>

    <!-- Name -->
    <span class="text-white/90 text-sm font-medium leading-none hidden sm:block">
      {{ user()?.fullName }}
    </span>

    <!-- Chevron -->
    <svg
      class="text-white/70 transition-transform duration-200"
      [class.rotate-180]="open()"
      width="12" height="12" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"
    >
      <path d="M6 9l6 6 6-6"/>
    </svg>
  </button>

  <!-- Dropdown panel -->
  @if (open()) {
    <div
      class="absolute right-0 top-full mt-2 w-80 max-w-[90vw] bg-surface border border-border shadow-lg z-50"
    >
      <!-- User info header -->
      <div class="px-4 py-3 border-b border-border">
        <p class="text-sm font-semibold text-[#222] truncate">{{ user()?.fullName }}</p>
        <p class="text-xs text-muted truncate mt-0.5">{{ user()?.emailAddress }}</p>
      </div>

      <!-- Warehouse -->
      <div class="px-4 py-3 border-b border-border relative">
        <p class="text-2xs text-muted uppercase tracking-wide font-medium mb-1.5">Warehouse</p>

        @if (warehouses().length > 1) {
          <button
            type="button"
            (click)="toggleWarehouseMenu($event)"
            class="w-full flex items-center justify-between gap-2 px-2.5 py-2 border border-border
                   bg-canvas hover:border-primary transition-colors duration-150 cursor-pointer
                   focus:outline-none focus:border-primary"
            [attr.aria-expanded]="warehouseMenuOpen()"
            aria-haspopup="listbox"
          >
            <span class="flex items-center gap-2 min-w-0">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                   stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-primary">
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"/>
              </svg>
              <span class="text-sm font-medium text-[#222] truncate" [title]="selectedWarehouseName()">
                {{ selectedWarehouseName() || 'Select warehouse' }}
              </span>
            </span>
            <svg
              width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
              stroke-linecap="round" class="text-muted shrink-0 transition-transform duration-200"
              [class.rotate-180]="warehouseMenuOpen()"
            >
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>

          @if (warehouseMenuOpen()) {
            <div
              class="dropdown-in absolute left-4 right-4 mt-1 bg-surface border border-border shadow-lg z-10
                     max-h-48 overflow-y-auto"
              role="listbox"
            >
              @for (wh of warehouses(); track wh.warehouseId) {
                <button
                  type="button"
                  role="option"
                  [attr.aria-selected]="wh.warehouseId === selectedWarehouseId()"
                  (click)="selectWarehouse(wh.warehouseId)"
                  class="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm
                         cursor-pointer transition-colors duration-100"
                  [class]="wh.warehouseId === selectedWarehouseId()
                    ? 'bg-primary-light text-primary font-semibold'
                    : 'text-[#222] hover:bg-canvas'"
                >
                  <span class="truncate">{{ wh.warehouseName }}</span>
                  @if (wh.warehouseId === selectedWarehouseId()) {
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                         stroke-linecap="round" stroke-linejoin="round" class="shrink-0">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  }
                </button>
              }
            </div>
          }
        } @else {
          <p class="text-sm text-[#222] font-medium truncate flex items-center gap-2" [title]="selectedWarehouseName()">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                 stroke-linecap="round" stroke-linejoin="round" class="shrink-0 text-primary">
              <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"/>
            </svg>
            {{ selectedWarehouseName() || '—' }}
          </p>
        }
      </div>

      <!-- Warehouse settings -->
      <div class="px-3 py-2 border-b border-border">
        <a
          routerLink="/settings/zip-codes"
          routerLinkActive="bg-primary-light text-primary font-semibold"
          (click)="close()"
          class="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#222]
                 no-underline hover:bg-canvas transition-colors duration-150 cursor-pointer"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          Serviceable Zip Codes
        </a>
        <a
          routerLink="/settings/store-timings"
          routerLinkActive="bg-primary-light text-primary font-semibold"
          (click)="close()"
          class="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-[#222]
                 no-underline hover:bg-canvas transition-colors duration-150 cursor-pointer"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <path d="M12 7v5l3 2"/>
          </svg>
          Store Timings
        </a>
      </div>

      <!-- Logout -->
      <div class="px-3 py-2">
        <button
          (click)="logout()"
          class="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-danger
                 hover:bg-red-50 transition-colors duration-150 cursor-pointer focus:outline-none"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Logout
        </button>
      </div>
    </div>
  }

</div>
